/* jshint maxcomplexity: 5, maxparams: 3, maxdepth: 2, maxstatements: 10 */

const shoppingCart = require('od-modules/cart/cart');
const {additionalProductsCatalog} = require('od-modules/track_events/track.frontevents.additional.products');

const BASIC_PLAN_TYPE = 'BASIC';
const PLUS_ERROR_COUNT_SERVICE_OPTIONS_TABLE = 'PLUS_ERROR_COUNT_SERVICE_OPTIONS_TABLE';
const SO_PRODUCT_ID_MAPPINGS = {
    'P': 'PREMIUM',
    'S': 'STANDARD',
    'B': 'BASIC',
};
const CART_REFRESH_ACTIONS = ['ADD', 'REMOVE'];

const getShoppingCartItems = (products = []) => shoppingCart.take.serviceOptions(({id}) => products.includes(id));

module.exports = (function() {
    'use strict';
    const PricePublisherMixin = require('../mixins/price_publisher.mixin');
    const trackingUtils = require('../utils/tracking.utils');
    return Odigeo.Model.extend(PricePublisherMixin.extend({
        phonePattern: /^\d{3,13}$/,
        dataUrl: 'getserviceoptiontable',
        defaults: {
            'phoneInfo': {},
            'isPhoneChanged': false,
            'avoidBlinkPhone': false,
            'keepPhoneAreaOpen': false,
        },

        init() {
            this.on('invalid', this.invalidEventHandler);
            shoppingCart.subscribe(this.refreshFromShoppingCart.bind(this));
        },

        invalidEventHandler(model, error) {
            error.apply(this, arguments);
        },

        validate(attrs) {
            if (!this.phonePattern.exec(attrs.phoneInfo.phoneNumber)) {
                return this.phoneNumberInvalid;
            }
        },

        phoneNumberInvalid() {
            this.widget.view.showInvalidPhoneError();
            this.widget.tracking.changePhoneIncorrect(this.getSelectedProductType());
        },

        getDataRequestSettings() {
            this.widget.tracking.willTrackOnLoadOfSomeProducts();

            return {
                type: 'GET',
                data: null,
                success: Odigeo._.bind(this.requestSuccessHandler, this),
                error: Odigeo._.bind(this.requestErrorHandler, this),
            };
        },

        url() {
            return Odigeo.Settings.getProperty('serviceOptionsUrlFormatter')(this.dataUrl);
        },

        requestErrorHandler() {
            trackingUtils.counterMetric(PLUS_ERROR_COUNT_SERVICE_OPTIONS_TABLE);
            this.widget.tracking.trackOfferedProducts(null);
        },

        requestSuccessHandler(response) {
            if (!Odigeo._.isEmpty(response)) {
                const products = this.parseProducts(response.columns);
                if (!Odigeo._.isEmpty(products)) {
                    this.setInitialProductEvolutionSelection(products);
                    this.set({
                        'phoneInfo': this.getPhoneInfo(response),
                        'services': response.options,
                        'serviceGroups': Odigeo._.map(response.options, 'groupId'),
                        'productTypes': Odigeo._.keys(response.columns),
                        'recommended': this.getRecommendedProduct(products),
                        'products': products,
                        'keepPhoneAreaOpen': this.get('isProductSelected'),
                        'debugInfo': response.debugInfo,
                        'hasToAvoidRejectedNag': response.hasToAvoidRejectedNag,
                    });
                    this.updateBreakdownPrices();

                    // Manually trigger change event cause BB does not detects it
                    this.trigger('change:keepPhoneAreaOpen', this);

                    this.trackLoadedProducts(products);
                }
            }

            this.addPrecheckedSPtoShoppingCart();
            this.loadProductsFromShoppingCart();
        },

        addPrecheckedSPtoShoppingCart() {
            const type = this.getSelectedProductType();
            const offerId = this.getSelectedProductOfferId();
            const getShoppingCartProductId = shoppingCart.builders.serviceOption.id(type, offerId).name;

            if (!shoppingCart.containsProduct(getShoppingCartProductId)
                && !shoppingCart.hasProductBeenRemoved(getShoppingCartProductId)
                && !Odigeo._.isEmpty(type)) {
                this.updateProductInShoppingCart(type, offerId, shoppingCart.sources.actions.PRESELECTED);
            }
        },

        loadProductsFromShoppingCart() {
            getShoppingCartItems(this.getOfferIdList())
                .forEach(({type}) => {
                    if (this.getSelectedProductType() !== type) {
                        this.toggleAncillaryOffer({type, select: true});
                    }
                });
        },

        refreshFromShoppingCart({action, data = {}} = {}) {
            const {type, category} = data;

            if (CART_REFRESH_ACTIONS.includes(action) && category === shoppingCart.categories.SERVICE_OPTION) {
                this.updateServiceOptions(type, false);
            } else if (action === 'UPDATE') {
                this.updateBreakdownPrices();
            }
        },

        getOfferIdList() {
            return Object.values(this.getProducts()).map(({offerId}) => offerId);
        },

        getProducts() {
            return this.get('products') || {};
        },

        trackLoadedProducts(products) {
            this.widget.tracking.trackOfferedProducts(this.getProductTypesToTrack(products));
        },

        getProductTypesToTrack(products = {}) {
            return Object.values(products).map(({type}) => type);
        },

        getPhoneInfo(response) {
            const phoneInfo = this.get('phoneInfo');
            const hasUsablePhoneInfo = Odigeo._.get(phoneInfo, 'phoneNumber', false);
            return this.get('isPhoneChanged') && hasUsablePhoneInfo ?
                phoneInfo :
                Odigeo._.pick(response, 'phoneCode', 'phoneNumber', 'phonePrefix');
        },

        refreshData() {
            this.isEmptyData = false;
            this.refresh();
        },

        parseProducts(products) {
            const parsedProducts = {};

            Odigeo._.each(products, (prod, productName) => {
                const offer = prod.offerDTO;
                parsedProducts[productName] = {
                    servicesTexts: prod.keys,
                    pbdText: prod.pbdText,
                    name: offer.name,
                    offerId: offer.offerId,
                    recommended: offer.recommended,
                    selectable: offer.selectable,
                    selected: offer.selected,
                    type: offer.type,
                    productStructure: offer.product,
                    pricing: {
                        onePrice: offer.onePrice,
                        quantity: offer.quantity,
                        totalPrice: offer.totalPrice,
                        segmentPassengerPrice: offer.segmentPassengerPriceHD,
                    },
                };
            });

            return parsedProducts;
        },

        getRecommendedProduct(products) {
            const selectedProduct = Odigeo._.filter(products, (product) => {
                return product.type !== 'basic' && product.selected;
            });
            const recommendedProduct = Odigeo._.find(products, {recommended: true});
            const recommendedType = Odigeo._.isEmpty(recommendedProduct) ? null : recommendedProduct.type;

            return Odigeo._.size(selectedProduct) > 0 ? null : recommendedType;
        },

        getPriceSelectedProduct(products) {
            const selectedProduct = Odigeo._.filter(products, (product) => {
                return product.type !== BASIC_PLAN_TYPE && product.selected;
            });
            return Odigeo._.size(selectedProduct) <= 0 ? 0 : selectedProduct[0].pricing.onePrice;
        },

        isProductSelected(products) {
            return !Odigeo._.isUndefined(products) && this.isProductSelectedWithProducts(products);
        },

        isProductSelectedWithProducts(products) {
            const selectedProductEvolution = this.findSelectedProduct(products);
            return !Odigeo._.isUndefined(Odigeo._.get(selectedProductEvolution, 'type'));
        },

        findSelectedProduct(products) {
            return Odigeo._.find(products, {selected: true});
        },

        setInitialProductEvolutionSelection(products) {
            const storedSelectedProductType = this.getSelectedProductType();
            const currentSelectedProduct = this.findSelectedProduct(products);
            const currentSelectedProductType = Odigeo._.get(currentSelectedProduct, 'type');
            let isProductSelected;

            if (storedSelectedProductType === currentSelectedProductType && !Odigeo._.isEmpty(currentSelectedProductType)) {
                isProductSelected = true;
            } else {
                const selectedProductType = !Odigeo._.isEmpty(storedSelectedProductType) ?
                    storedSelectedProductType : currentSelectedProductType;
                this.selectProductByType(products, selectedProductType);
                isProductSelected = !Odigeo._.isEmpty(selectedProductType);
            }

            this.set('isProductSelected', isProductSelected);
        },

        selectProductByType(products, selectedProductType) {
            Odigeo._.each(products, (product) => {
                product.selected = product.type === selectedProductType;
                product.label = additionalProductsCatalog.SERVICE_OPTIONS.label(product.type);
            });
        },

        clearSelection() {
            const products = this.get('products') || {};
            Object.entries(products).forEach(([_, product]) => product.selected = false);
        },

        getProductPriceBreakdown(selectedProduct) {
            let pbItem = {};
            const productPriceInfo = Odigeo._.get(selectedProduct, 'pricing');

            Odigeo._.extend(productPriceInfo, {'type': 'SERVICE_OPTIONS'});
            if (!Odigeo._.isUndefined(productPriceInfo)) {
                pbItem = {
                    'conceptLabel': selectedProduct.pbdText,
                    'conceptKey': selectedProduct.type,
                    'onePrice': productPriceInfo.onePrice,
                    'allPrice': productPriceInfo.totalPrice,
                    'quantity': productPriceInfo.quantity,
                    'type': productPriceInfo.type,
                };
            }
            return pbItem;
        },

        toggleAncillaryOfferIfExists(productId, select) {
            if (SO_PRODUCT_ID_MAPPINGS[productId]) {
                this.toggleAncillaryOffer({productId, select});
            }
        },

        toggleAncillaryOffer({productId, select = false, type}) {
            const productType = type || this.getProductType(productId);
            this.updateServiceOptions(select ? productType : BASIC_PLAN_TYPE);
        },

        getProductType(identifier) {
            return SO_PRODUCT_ID_MAPPINGS[identifier] || BASIC_PLAN_TYPE;
        },

        updateServiceOptions(type, updateCart = true) {
            const products = this.get('products');
            const previousSelectedType = this.getSelectedProductType();
            const previousSelectedOfferId = this.getSelectedProductOfferId();

            if (previousSelectedType !== type) {
                this.selectProductByType(products, type);
                this.set({
                    'recommended': this.getRecommendedProduct(products),
                    'avoidBlinkPhone': !Odigeo._.includes([type, previousSelectedType], BASIC_PLAN_TYPE),
                    'isProductSelected': this.isProductSelected(products),
                    'products': products,
                    'selectedProductPrice': this.getPriceSelectedProduct(products),
                });

                // Manually trigger change event cause BB does not detects it
                this.trigger('change:products', this);

                this.triggerUpdatedServiceOptions(type);

                this.updateBreakdownPrices();

                if (updateCart) {
                    this.updateProductInShoppingCart(previousSelectedType, previousSelectedOfferId, shoppingCart.sources.actions.USER_REQUESTED);
                }
            }
        },

        updateProductInShoppingCart(previousType, previousId, source = shoppingCart.sources.actions.USER_REQUESTED) {
            const {offerId, type, pbdText, pricing = {}} = this.getSelectedProduct();
            const {totalPrice = 0} = pricing;

            shoppingCart.builders.serviceOption.id(previousType, previousId).remove();

            shoppingCart.builders.serviceOption.data({
                id: offerId,
                type,
                concept: pbdText,
                price: {
                    total: totalPrice,
                },
            }).from(source, shoppingCart.sources.targets.WIDGET).add();
        },

        getSelectedProductOfferId() {
            const {offerId} = this.getSelectedProduct();

            return offerId;
        },

        triggerUpdatedServiceOptions(type) {
            const id = this.widget.id;

            for (let t of Object.keys(SO_PRODUCT_ID_MAPPINGS)) {
                const selected = SO_PRODUCT_ID_MAPPINGS[t] === type;
                Odigeo.trigger(`${id}:updatedServiceOptions`, t, selected);
            }
        },

        updateBreakdownPrices() {
            const serviceOptionSelected = this.getSelectedProduct();

            if (!Odigeo._.isEmpty(serviceOptionSelected)) {
                const pbItem = this.getProductPriceBreakdown(serviceOptionSelected);
                const dataShoppingCart = {
                    data: {
                        offerId: serviceOptionSelected.offerId,
                        postsellServiceOptionRequest: this.getPostsellServiceOptionRequest(serviceOptionSelected.productStructure),
                    },
                    id: 'serviceoptions',
                    type: 'success',
                };

                this.addItemToPricemanagerCollection(pbItem);

                Odigeo.trigger('complete:serviceoptions', dataShoppingCart);
                Odigeo.trigger(this.widget.id + ':productSelection', {'concept': pbItem.conceptLabel});
            }
        },

        getMonitoringAlertIdSelected(productStructure) {
            let alertId = '';
            Odigeo._.each(productStructure, (product) => {
                if (Odigeo._.has(product.monitoringAlert, 'id')) {
                    alertId = product.monitoringAlert.id;
                }
            });
            return alertId;
        },

        getPostsellServiceOptionRequest(productStructure) {
            const phoneInfo = this.get('phoneInfo');

            return Odigeo._.size(productStructure) ? {
                monitoringAlertRequest: [
                    {
                        id: this.getMonitoringAlertIdSelected(productStructure),
                        phone: {
                            phoneCountry: phoneInfo.phoneCode,
                            phoneNumber: phoneInfo.phoneNumber,
                        },
                    },
                ],
            } : {};
        },

        updatePhoneInfo(value) {
            this.set('avoidBlinkPhone', true);
            let phoneInfo = this.get('phoneInfo');
            let changedPhone = value.phoneCode != phoneInfo.phoneCode
                || value.phoneNumber != phoneInfo.phoneNumber
                || value.phonePrefix != phoneInfo.phonePrefix;
            this.set({'phoneInfo': value}, {validate: true});

            if (!this.validationError) {
                this.set('isPhoneChanged', changedPhone);
                this.trigger('change:phoneInfo', this);
                this.updateBreakdownPrices();
                this.widget.tracking.changePhoneNumberOk(this.getSelectedProductType());
            }
        },

        getSelectedProduct() {
            return this.findSelectedProduct(this.get('products')) || {};
        },

        getSelectedProductOrBasic() {
            const findSelectedProduct = this.findSelectedProduct(this.get('products'));
            const product = findSelectedProduct || this.findBasicProduct(this.get('products'));
            return !product ? null : {
                offerId: product.offerId,
                postsellServiceOptionRequest: this.getPostsellServiceOptionRequest(product.productStructure),
            };
        },

        findBasicProduct(products) {
            return Odigeo._.find(products, {type: BASIC_PLAN_TYPE});
        },

        getAvailableProducts() {
            return Odigeo._.map(this.get('products'), (product) => product.type);
        },

        getAddedProduct() {
            const product = this.findSelectedProduct(this.get('products'));
            return product ? product.type : null;
        },

        validateConditionSoEvolutionAdded() {
            return !!this.get('isProductSelected') || Odigeo._.isEmpty(this.get('products'));
        },

        addBasicSo() {
            this.updateServiceOptions(BASIC_PLAN_TYPE);
        },

        addRecommendedSo() {
            const recommendedProduct = Odigeo._.find(this.get('products'), {recommended: true});
            const recommendedType = Odigeo._.isEmpty(recommendedProduct) ? null : recommendedProduct.type;
            this.updateServiceOptions(recommendedType);
        },

        getSelectedProductType() {
            const {type} = this.getSelectedProduct();
            return type;
        },

        isBasicSelected() {
            return this.getSelectedProductType() === BASIC_PLAN_TYPE;
        },

        hasToAvoidNag() {
            return this.get('hasToAvoidRejectedNag') || Odigeo.AB.isSupportPackStepNoNagEnabled;
        },
    }));
}());
